import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import SchemaCorporel from './SchemaCorporel'

function initiales(prenom, nom) {
  return ((prenom?.[0] || '') + (nom?.[0] || '')).toUpperCase()
}

// Affiche la date/heure telle que stockée, sans conversion de fuseau (heure "brute")
function dateHeureBrute(s) {
  if (!s) return ''
  const d = String(s).slice(0, 10), t = String(s).slice(11, 16)
  if (d.length < 10) return String(s)
  return d.slice(8, 10) + '/' + d.slice(5, 7) + '/' + d.slice(0, 4) + (t ? ' ' + t : '')
}

function getAge(dateNaissance) {
  if (!dateNaissance) return null
  const diff = Date.now() - new Date(dateNaissance).getTime()
  return Math.floor(diff / (1000 * 60 * 60 * 24 * 365.25))
}

export default function Consultations() {
  const { praticien } = useAuth()
  const navigate = useNavigate()
  const [consultations, setConsultations] = useState([])
  const [patients, setPatients] = useState([])
  const [loading, setLoading] = useState(false)
  const [panel, setPanel] = useState(null)
  const panelRef = useRef(null)

  function openPanel(val) {
    panelRef.current = val
    setPanel(val)
  }
  function closePanel() {
    panelRef.current = null
    setPanel(null)
    load()
  }
  const [form, setForm] = useState(emptyForm())
  const [saving, setSaving] = useState(false)
  const [envoyerEmail, setEnvoyerEmail] = useState(false)
  const [search, setSearch] = useState('')
  const [autoSaveStatus, setAutoSaveStatus] = useState(null)
  const autoSaveTimer = useRef(null)
  const currentConsultId = useRef(null)
  const [patientSearch, setPatientSearch] = useState('')
  const [patientSearchOpen, setPatientSearchOpen] = useState(false)
  const [patientActuel, setPatientActuel] = useState(null) // données complètes du patient sélectionné
  // [MAIL-PRECOCHE v1] re-coche automatiquement l'envoi mail quand un patient avec e-mail est chargé
  useEffect(() => { setEnvoyerEmail(!!patientActuel?.email) }, [patientActuel])
  const [panelPatient, setPanelPatient] = useState(false) // pour ouvrir la fiche patient

  // Consultations importées de Terapiz : notes_seance peut contenir un formulaire sérialisé en JSON.
  // On le répartit dans les bons champs (motif détaillé / traitement / notes). Non destructif.
  function parseNotes(raw) {
    if (!raw) return ''
    if (raw.startsWith('{') || raw.startsWith('"')) {
      try {
        const parsed = JSON.parse(raw)
        return Object.entries(parsed)
          .filter(([k, v]) => v && typeof v === 'string' && v.trim() && !k.startsWith('select-'))
          .map(([, v]) => v.replace(/\\n/g, '\n').replace(/\\u00e9/g, 'é').replace(/\\u00e8/g, 'è').replace(/\\u00ea/g, 'ê').replace(/\\u00e0/g, 'à'))
          .filter(v => v.trim())
          .join('\n')
      } catch {
        return raw.replace(/\\n/g, '\n').replace(/[{}"]/g, '').replace(/"select-[^"]+":"[^"]*",?/g, '').trim()
      }
    }
    return raw
  }

  function splitImportedNotes(c) {
    const raw = c?.notes_seance || ''
    if (!raw.startsWith('{')) {
      return { motif_detail: c?.motif_detail || '', traitement: c?.traitement || '', notes_seance: raw }
    }
    let obj = null
    try { obj = JSON.parse(raw) } catch { obj = null }
    if (!obj) {
      return { motif_detail: c?.motif_detail || '', traitement: c?.traitement || '', notes_seance: parseNotes(raw) }
    }
    const clean = v => (typeof v === 'string' ? v.trim() : '')
    const motif = clean(obj['textarea-1674831374139-0']) || clean(obj['textarea-1643723666948-0'])
    const traitement = clean(obj['textarea-1674831462691-0']) || clean(obj['textarea-1606906805766'])
    const pris = new Set(['textarea-1674831374139-0', 'textarea-1643723666948-0', 'textarea-1674831462691-0', 'textarea-1606906805766'])
    const reste = Object.entries(obj)
      .filter(([k, v]) => k.startsWith('textarea-') && !pris.has(k) && clean(v))
      .map(([, v]) => clean(v)).join('\n')
    if (motif || traitement) {
      return {
        motif_detail: clean(c?.motif_detail) || motif,
        traitement: clean(c?.traitement) || traitement,
        notes_seance: reste
      }
    }
    return { motif_detail: c?.motif_detail || '', traitement: c?.traitement || '', notes_seance: parseNotes(raw) }
  }

  function emptyForm() {
    return {
      patient_id: '', date_consultation: new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16),
      duree_minutes: 45, motif: '', motif_detail: '', traitement: '', notes_seance: '', tarif: 65
    }
  }

  const load = useCallback(async () => {
    if (panelRef.current) return
    setLoading(true)
    const { data } = await supabase
      .from('consultations')
      .select('*, patients(id, prenom, nom, date_naissance, email), praticiens(prenom, nom)')
      .order('date_consultation', { ascending: false })
      .limit(100)
    setConsultations(data || [])
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  // Ouvrir automatiquement une consultation si navigation depuis fiche patient
  useEffect(() => {
    const consultId = localStorage.getItem('open_consultation_id')
    if (consultId) {
      localStorage.removeItem('open_consultation_id')
      navigate('/agenda?consult=' + consultId) // [UNIFIE-PANEL v1] rediriger vers le panneau agenda
    }
  }, [])

  useEffect(() => {
    const handler = () => navigate('/agenda') // [UNIFIE-PANEL v1] page Consultations = recherche seule ; création/édition via l'agenda
    window.addEventListener('cabinet:new', handler)
    return () => window.removeEventListener('cabinet:new', handler)
  }, [])

  useEffect(() => {
    const fetchAll = async () => {
      let all = [], from = 0
      while (true) {
        const { data } = await supabase.from('patients').select('id, prenom, nom, date_naissance, email').order('nom').range(from, from + 999)
        if (!data || data.length === 0) break
        all = [...all, ...data]
        if (data.length < 1000) break
        from += 1000
      }
      setPatients(all)
    }
    fetchAll()
  }, [])

  useEffect(() => {
    if (!panel || panel === 'new' || !currentConsultId.current) return
    autoSaveTimer.current = setInterval(() => { autoSave() }, 30000)
    return () => clearInterval(autoSaveTimer.current)
  }, [panel, form])

  async function autoSave() {
    if (!currentConsultId.current || !form.patient_id) return
    setAutoSaveStatus('saving')
    await supabase.from('consultations').update({
      patient_id: form.patient_id, praticien_id: praticien.id,
      date_consultation: form.date_consultation, duree_minutes: parseInt(form.duree_minutes),
      motif: form.motif, motif_detail: form.motif_detail, traitement: form.traitement,
      notes_seance: form.notes_seance, tarif: parseFloat(form.tarif)
    }).eq('id', currentConsultId.current)
    setAutoSaveStatus('saved')
    setTimeout(() => setAutoSaveStatus(null), 2000)
  }

  async function openNew() {
    setForm(emptyForm())
    setPatientSearch('')
    setPatientActuel(null)
    currentConsultId.current = null
    openPanel('new')
  }

  async function openEdit(c) {
    const champsT = splitImportedNotes(c)
    setForm({
      patient_id: c.patient_id,
      date_consultation: c.date_consultation.slice(0,16),
      duree_minutes: c.duree_minutes,
      motif: c.motif || '', motif_detail: champsT.motif_detail,
      traitement: champsT.traitement, notes_seance: champsT.notes_seance,
      tarif: c.tarif
    })
    setPatientSearch((c.patients?.prenom || '') + ' ' + (c.patients?.nom || ''))
    setPatientActuel(c.patients || null)
    currentConsultId.current = c.id
    openPanel(c)
  }

  async function selectPatient(p) {
    setForm(f => ({...f, patient_id: p.id}))
    setPatientSearch(p.prenom + ' ' + p.nom)
    setPatientSearchOpen(false)
    // Charger les données complètes du patient + liens famille
    const [{ data }, { data: liens }] = await Promise.all([
      supabase.from('patients').select('*').eq('id', p.id).single(),
      supabase.from('patient_liens').select('*').eq('patient_id', p.id)
    ])
    const patient = data || p
    patient._aParent = liens && liens.length > 0
    setPatientActuel(patient)
  }

  async function save() {
    setSaving(true)
    const payload = {
      patient_id: form.patient_id, praticien_id: praticien.id,
      date_consultation: form.date_consultation, duree_minutes: parseInt(form.duree_minutes),
      motif: form.motif, motif_detail: form.motif_detail, traitement: form.traitement,
      notes_seance: form.notes_seance, tarif: parseFloat(form.tarif)
    }
    let consultId = currentConsultId.current
    if (panel === 'new') {
      const { data, error } = await supabase.from('consultations').insert(payload).select('id').limit(1)
      if (!error && data && data[0]) {
        consultId = data[0].id
        currentConsultId.current = consultId
      }
    } else {
      await supabase.from('consultations').update(payload).eq('id', panel.id)
      consultId = panel.id
    }
    setSaving(false)
    if (panel === 'new') {
      openPanel({ id: consultId, _keepOpen: true })
      setAutoSaveStatus('saved')
      setTimeout(() => setAutoSaveStatus(null), 2000)
    } else {
      setAutoSaveStatus('saved')
      setTimeout(() => setAutoSaveStatus(null), 2000)
    }
    return consultId
  }

  async function saveAndBill() {
    // Vérifier les données patient avant de facturer
    const age = patientActuel ? getAge(patientActuel.date_naissance) : null
    const warnings = []
    if (!patientActuel?.email) warnings.push('• Email manquant')
    if (!patientActuel?.date_naissance) warnings.push('• Date de naissance manquante')
    if (age !== null && age < 18 && !patientActuel?._aParent) warnings.push('• Patient mineur — pensez à associer un parent dans sa fiche')

    if (warnings.length > 0) {
      const continuer = window.confirm(
        'Attention, la fiche patient est incomplète :\n\n' + warnings.join('\n') +
        '\n\nVoulez-vous quand même créer la facture ?'
      )
      if (!continuer) return
    }

    setSaving(true)
    const consultId = await save()
    if (consultId && form.patient_id) {
      const annee = new Date().getFullYear()
      const ts = Date.now()
      const numero = `FAC-${annee}-${ts}`
      const { data: nouvelleFacture } = await supabase.from('factures').insert({
        numero,
        praticien_id: praticien.id,
        patient_id: form.patient_id,
        consultation_id: consultId,
        date_facture: form.date_consultation.slice(0, 10),
        montant: parseFloat(form.tarif),
        statut: 'payee',
        mode_paiement: 'cb',
      }).select().single()
      // Recette comptable (sinon l'acte n'apparaît pas en Comptabilité)
      const { error: eEcriture } = await supabase.from('ecritures_comptables').insert({
        praticien_id: praticien.id,
        date_ecriture: form.date_consultation.slice(0, 10),
        libelle: 'Consultation ' + (patientActuel?.prenom || '') + ' ' + (patientActuel?.nom || ''),
        type: 'recette', categorie: 'Honoraires',
        montant: parseFloat(form.tarif), source: 'auto',
        facture_id: nouvelleFacture?.id || null, mode_paiement: 'cb',
      })
      if (eEcriture) alert('⚠ Facture créée, mais la recette comptable n\'a PAS été enregistrée : ' + eEcriture.message + '\nÀ vérifier dans Comptabilité.')
      // Rendez-vous (pour que l'acte apparaisse dans l'agenda), déjà honoré
      await supabase.from('rendez_vous').insert({
        patient_id: form.patient_id, praticien_id: praticien.id,
        date_rdv: form.date_consultation, duree_minutes: parseInt(form.duree_minutes) || 45,
        motif: form.motif || '', statut: 'honore', notes: ''
      })
      // Envoyer la facture par email si demandé
      if (envoyerEmail && patientActuel?.email) {
        try {
          await fetch('https://qzaqbhmtmeokgbdywjgx.supabase.co/functions/v1/send-facture', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              email: patientActuel.email,
              prenomPatient: patientActuel.prenom,
              numero,
              dateFacture: new Date(form.date_consultation).toLocaleDateString('fr-FR'),
              montant: parseFloat(form.tarif),
              praticienPrenom: praticien.prenom,
              praticienNom: praticien.nom,
            })
          })
        } catch(e) { console.error('Email non envoyé', e) }
      }
    }
    setSaving(false)
    setAutoSaveStatus('saved')
    setTimeout(() => setAutoSaveStatus(null), 2000)
  }

  const patientsFiltered = patientSearch.length >= 1
    ? patients.filter(p => {
        const mots = patientSearch.toLowerCase().trim().split(/\s+/).filter(Boolean)
        const t = ((p.prenom||'') + ' ' + (p.nom||'')).toLowerCase()
        return mots.every(m => t.includes(m))
      }).slice(0, 8)
    : []

  const filtered = search
    ? consultations.filter(c => {
        const mots = search.toLowerCase().trim().split(/\s+/).filter(Boolean)
        const t = `${c.patients?.prenom} ${c.patients?.nom} ${c.praticiens?.prenom} ${c.praticiens?.nom}`.toLowerCase()
        return mots.every(m => t.includes(m))
      })
    : consultations

  // Alertes patient actuel
  const agePatient = patientActuel ? getAge(patientActuel.date_naissance) : null
  const alertes = []
  if (patientActuel) {
    if (!patientActuel.email) alertes.push({ type: 'warn', msg: 'Email manquant dans la fiche patient' })
    if (!patientActuel.date_naissance) alertes.push({ type: 'warn', msg: 'Date de naissance manquante' })
    if (agePatient !== null && agePatient < 18 && !patientActuel?._aParent) alertes.push({ type: 'danger', msg: `Patient mineur (${agePatient} ans) — pensez à associer un parent dans sa fiche` })
  }

  return (
    <>
      <div className="section-header">
        <span className="section-title">Historique des consultations</span>
      </div>

      <div className="search-bar">
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="6.5" cy="6.5" r="4.5"/><path d="M10.5 10.5l3 3"/></svg>
        <input placeholder="Rechercher par patient ou praticienne…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {loading ? <div className="empty">Chargement…</div> : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>Date</th><th>Patient</th><th>Praticienne</th><th>Motif</th><th>Durée</th><th>Tarif</th></tr>
            </thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={6} className="empty">Aucune consultation</td></tr>}
              {filtered.map(c => (
                <tr key={c.id} className="clickable" onClick={() => navigate('/agenda?consult=' + c.id)}>
                  <td className="td-muted">{dateHeureBrute(c.date_consultation)}</td>
                  <td>
                    <div className="cell-name">
                      <div className={`avatar ${c.patients?.nom?.charCodeAt(0) % 2 === 0 ? 'v2' : ''}`}>
                        {initiales(c.patients?.prenom, c.patients?.nom)}
                      </div>
                      <span
                        onClick={e => { e.stopPropagation(); localStorage.setItem('open_patient_id', c.patients?.id); navigate('/patients') }}
                        style={{textDecoration:'underline', cursor:'pointer', color:'var(--primary)'}}>
                        {c.patients?.prenom} {c.patients?.nom}
                      </span>
                    </div>
                  </td>
                  <td className="td-muted">{c.praticiens?.prenom} {c.praticiens?.nom}</td>
                  <td className="td-muted">{c.motif || '—'}</td>
                  <td className="td-muted">{c.duree_minutes} min</td>
                  <td>{c.tarif} €</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {panel && (
        <>
          <div className="panel-overlay" onClick={() => { closePanel() }} />
          <div className="panel">
            <div className="panel-header">
              <h3>{panel === 'new' ? 'Nouvelle consultation' : 'Modifier la consultation'}</h3>
              <div style={{display:'flex', alignItems:'center', gap:8}}>
                {autoSaveStatus === 'saving' && <span style={{fontSize:11, color:'var(--text-3)'}}>Sauvegarde…</span>}
                {autoSaveStatus === 'saved' && <span style={{fontSize:11, color:'#2E7D32'}}>✓ Sauvegardé</span>}
                <button className="close-btn" onClick={() => { closePanel() }}>✕</button>
              </div>
            </div>
            <div className="panel-body">

              {/* Recherche patient */}
              <div className="form-group" style={{marginBottom: alertes.length > 0 ? 6 : 14}}>
                <label>Patient *
                  {patientActuel && (
                    <span onClick={() => { localStorage.setItem('open_patient_id', patientActuel.id); navigate('/patients') }}
                      style={{fontSize:11, marginLeft:8, color:'var(--primary)', textDecoration:'underline', cursor:'pointer'}}>
                      → Ouvrir la fiche
                    </span>
                  )}
                </label>
                <div style={{position:'relative'}}>
                  <input
                    placeholder="Rechercher par nom ou prénom…"
                    value={patientSearch}
                    autoComplete="off"
                    onChange={e => { setPatientSearch(e.target.value); setForm(f => ({...f, patient_id: ''})); setPatientActuel(null) }}
                    onFocus={() => setPatientSearchOpen(true)}
                  />
                  {patientSearchOpen && patientSearch.length >= 1 && (
                    <div style={{position:'absolute', top:'100%', left:0, right:0, zIndex:100, background:'var(--surface)', border:'1px solid var(--border-md)', borderRadius:'var(--radius)', maxHeight:200, overflowY:'auto', boxShadow:'0 4px 12px rgba(0,0,0,.1)'}}>
                      {patientsFiltered.length === 0 && <div style={{padding:'8px 12px', fontSize:12, color:'var(--text-3)'}}>Aucun patient trouvé</div>}
                      {patientsFiltered.map(p => {
                        const age = getAge(p.date_naissance)
                        return (
                          <div key={p.id}
                            onMouseDown={() => selectPatient(p)}
                            style={{padding:'8px 12px', cursor:'pointer', fontSize:13, borderBottom:'0.5px solid var(--border)'}}
                            onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
                            onMouseLeave={e => e.currentTarget.style.background = ''}>
                            <span style={{fontWeight:500}}>{p.prenom} {p.nom}</span>
                            {p.date_naissance && <span style={{fontSize:11, color:'var(--text-3)', marginLeft:8}}>{new Date(p.date_naissance).toLocaleDateString('fr-FR')}</span>}
                            {age !== null && age < 18 && <span style={{fontSize:10, color:'#C62828', marginLeft:6, fontWeight:600}}>MINEUR</span>}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
                {form.patient_id && <div style={{fontSize:11, color:'var(--green-text)', marginTop:3}}>✓ Patient sélectionné</div>}
              </div>

              {/* Alertes patient */}
              {alertes.map((a, i) => (
                <div key={i} style={{
                  padding:'8px 12px', borderRadius:'var(--radius)', marginBottom:8, fontSize:12, fontWeight:500,
                  background: a.type === 'danger' ? '#FFEBEE' : '#FFF8E1',
                  color: a.type === 'danger' ? '#C62828' : '#E65100',
                  border: `1px solid ${a.type === 'danger' ? '#FFCDD2' : '#FFE082'}`
                }}>
                  {a.type === 'danger' ? '⚠️ ' : '⚠ '}{a.msg}
                  {patientActuel && (
                    <span onClick={() => { localStorage.setItem('open_patient_id', patientActuel.id); navigate('/patients') }}
                      style={{marginLeft:8, textDecoration:'underline', color:'inherit', cursor:'pointer'}}>
                      Compléter la fiche →
                    </span>
                  )}
                </div>
              ))}

              <div className="form-row">
                <div className="form-group">
                  <label>Date et heure *</label>
                  <input type="datetime-local" value={form.date_consultation} onChange={e => setForm({...form, date_consultation: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Durée (min)</label>
                  <select value={form.duree_minutes} onChange={e => setForm({...form, duree_minutes: e.target.value})}>
                    <option value={30}>30 min</option>
                    <option value={45}>45 min</option>
                    <option value={60}>60 min</option>
                    <option value={75}>75 min</option>
                    <option value={90}>90 min</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Motif</label>
                  <input value={form.motif} onChange={e => setForm({...form, motif: e.target.value})} placeholder="Ex: Douleur lombaire" />
                </div>
                <div className="form-group">
                  <label>Tarif (€)</label>
                  <input type="number" value={form.tarif} onChange={e => setForm({...form, tarif: e.target.value})} />
                </div>
              </div>
              <div className="form-group" style={{marginBottom:14}}>
                <label>Détail du motif</label>
                <textarea value={form.motif_detail} onChange={e => setForm({...form, motif_detail: e.target.value})}
                  placeholder="Description détaillée de la plainte, localisation, depuis quand, facteurs aggravants/soulageants…"
                  style={{minHeight: 80}} />
              </div>
              <div className="form-group" style={{marginBottom:14}}>
                <label>Traitement</label>
                <textarea value={form.traitement} onChange={e => setForm({...form, traitement: e.target.value})}
                  placeholder="Techniques utilisées, zones travaillées, manipulations…"
                  style={{minHeight: 80}} />
              </div>
              <div className="form-group">
                <label>Notes</label>
                <textarea value={form.notes_seance} onChange={e => setForm({...form, notes_seance: e.target.value})}
                  placeholder="Évolution, résultat, suite à donner, conseils donnés au patient…"
                  style={{minHeight: 80}} />
              </div>

              {/* Schéma corporel — disponible si patient sélectionné */}
              {form.patient_id && (
                <div className="form-group" style={{marginTop:8}}>
                  <label>Schéma corporel</label>
                  <div style={{background:'var(--bg)', borderRadius:'var(--radius)', padding:12, border:'0.5px solid var(--border)'}}>
                    <SchemaCorporel
                      patientId={form.patient_id}
                      consultationId={currentConsultId.current}
                      readOnly={false}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="panel-footer" style={{flexWrap:'wrap', gap:8}}>
              <button className="btn btn-sm" onClick={() => { closePanel() }}>Annuler</button>
              <button className="btn btn-sm" onClick={save}
                disabled={saving || !form.patient_id || !form.date_consultation}>
                {saving ? '…' : 'Enregistrer'}
              </button>
              <div style={{display:'flex', alignItems:'center', gap:6, marginLeft:'auto'}}>
                {patientActuel?.email && (
                  <label style={{display:'flex', alignItems:'center', gap:5, fontSize:12, color:'var(--text-2)', cursor:'pointer'}}>
                    <input type="checkbox" checked={envoyerEmail} onChange={e => setEnvoyerEmail(e.target.checked)} />
                    Envoyer au patient
                  </label>
                )}
                <button className="btn btn-primary btn-sm"
                  style={{background:'linear-gradient(135deg,#1D9E75,#158A63)', border:'none'}}
                  onClick={saveAndBill}
                  disabled={saving || !form.patient_id || !form.date_consultation}>
                  {saving ? '…' : '✓ Enregistrer + Facturer'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  )
}
